import useSWR from 'swr'
import { API_URL } from '@/utils/constants'

/**
 * Fair-value marks for collection-backed holdings.
 *
 * Wallet totals have been computed at collection floor, which is the cheapest
 * live *ask*: an upper bound on what it costs to buy another one, and close to
 * meaningless as an estimate of what a holder's item is worth. It is also the
 * easiest number on the marketplace to move — one cheap listing reprices every
 * holder's displayed net worth until it is cancelled.
 *
 * The API replaces it with a mark to the middle of the live book, pulled
 * toward the median recent sale in proportion to how much trading there is to
 * learn from, and clamped between the best standing bid and the floor. See
 * `utils/pricing.rs` in ordinals-api for the model.
 *
 * Every mark carries a band and a confidence. Callers are expected to render
 * them: the point of this work is to stop showing estimates with the authority
 * of quotes, and a component that drops the band re-creates the problem.
 */

/** Which evidence produced a mark. Mirrors `MarkMethod` in the API. */
export type MarkMethod =
	| 'book-and-tape'
	| 'book-midpoint'
	| 'discounted-floor'
	| 'bid-only'
	| 'tape-only'
	| 'unpriced'

export interface Valuation {
	slug: string
	/** The mark. `null` means genuinely unpriceable — render "no market",
	 * never zero, or a wallet total silently understates itself. */
	fair_sats: number | null
	fair_usd: number | null
	/** Conservative end: what could be realised now, typically the best bid. */
	low_sats: number | null
	low_usd: number | null
	/** Optimistic end: replacement cost, typically the floor. */
	high_sats: number | null
	high_usd: number | null
	/** 0 = a guess, 1 = a liquid two-sided market with a deep tape. */
	confidence: number
	method: MarkMethod
	inputs: {
		best_bid_sats: number | null
		floor_sats: number | null
		tape_sats: number | null
		bid_depth: number
		trades_7d: number
		trades_30d: number
		last_sale_sats: number | null
		last_sale_ts: number | null
	}
	supply: number
	marketcap_sats: number | null
	marketcap_usd: number | null
}

const fetcher = (url: string) =>
	fetch(url).then((r) => {
		if (!r.ok) {
			throw new Error(`valuation ${r.status}`)
		}
		return r.json()
	})

/** Plain-English gloss on how a mark was reached, for a tooltip. */
export function methodLabel(method: MarkMethod): string {
	switch (method) {
		case 'book-and-tape':
			return 'Between the best offer and the lowest listing, weighted toward recent sales.'
		case 'book-midpoint':
			return 'Midway between the best offer and the lowest listing. Too few recent sales to say more.'
		case 'discounted-floor':
			return 'Based on the lowest listing, discounted because there are no offers.'
		case 'bid-only':
			return 'Based on the best live offer. Nothing is currently listed.'
		case 'tape-only':
			return 'Based on recent sales. There is no live offer or listing.'
		case 'unpriced':
		default:
			return 'No offers, no listings and no recent sales — there is no market to price against.'
	}
}

/** Confidence bucketed for display. */
export function confidenceLabel(confidence: number): 'high' | 'medium' | 'low' {
	if (confidence >= 0.66) {
		return 'high'
	}
	return confidence >= 0.33 ? 'medium' : 'low'
}

/** One collection's mark. */
export default function useValuation(slug: string | null | undefined) {
	const { data, error, isLoading } = useSWR<Valuation>(
		slug ? `${API_URL}/collection/${encodeURIComponent(slug)}/valuation` : null,
		fetcher,
		{ refreshInterval: 60_000, revalidateOnFocus: false, keepPreviousData: true }
	)
	return { valuation: data, error, isLoading }
}

/**
 * Marks for many collections in one request.
 *
 * A wallet page must not issue one request per collection held — at twenty
 * collections that is twenty round trips before a total can be shown. Slugs
 * are sorted into the cache key so that the same holdings in a different order
 * hit the same cache entry.
 */
export function useValuations(slugs: string[]) {
	const key = slugs.length ? ['valuations', [...slugs].sort().join(',')] : null

	const { data, error, isLoading } = useSWR<Record<string, Valuation>>(
		key,
		async () => {
			const res = await fetch(`${API_URL}/collections/valuation`, {
				method: 'POST',
				headers: { 'Content-Type': 'application/json' },
				body: JSON.stringify({ slugs })
			})
			if (!res.ok) {
				throw new Error(`valuations ${res.status}`)
			}
			const json = await res.json()
			const list: Valuation[] = Array.isArray(json?.valuations) ? json.valuations : []
			return Object.fromEntries(list.map((v) => [v.slug, v]))
		},
		{ refreshInterval: 60_000, revalidateOnFocus: false, keepPreviousData: true }
	)

	return { valuations: data ?? {}, error, isLoading }
}

/**
 * Total a set of holdings at fair value.
 *
 * Unpriceable collections are counted separately rather than as zero. A total
 * that silently absorbs them understates the wallet and gives no hint that it
 * has — so the count is returned and the UI is expected to disclose it.
 */
export function totalAtFairValue(
	holdings: Array<{ slug: string; count: number }>,
	valuations: Record<string, Valuation>
): { sats: number; usd: number; unpricedCollections: number; unpricedItems: number } {
	let sats = 0
	let usd = 0
	let unpricedCollections = 0
	let unpricedItems = 0

	for (const { slug, count } of holdings) {
		const v = valuations[slug]
		if (!v || v.fair_sats == null) {
			unpricedCollections += 1
			unpricedItems += count
			continue
		}
		sats += v.fair_sats * count
		usd += (v.fair_usd ?? 0) * count
	}

	return { sats, usd, unpricedCollections, unpricedItems }
}
